import React from 'react';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ALetterIcon from './shared/icons/ALetter';
import HLetterIcon from './shared/icons/HLetter';
import BLetterIcon from './shared/icons/BLetter';
import PLetterIcon from './shared/icons/PLetter';
import CompensationForm from './shared/forms/CompensationForm/CompensationForm';
import ScreeningForm from './shared/forms/ScreeningForm/ScreeningForm';
import AdditionalActivitiesForm from './shared/forms/AdditionalActivitiesForm/AdditionalActivitiesForm';
import OfferForm from './shared/forms/OfferForm/OfferForm';
import { DockIcon, ChecklistIcon, TestTaskIcon, RoleRelatedIcon, GeneralInterviewIcon } from 'framework/ui/mui-builder';
import GeneralInterviewScheduledForm from './shared/forms/InterviewScheduledForm/GeneralInterviewScheduledForm';
import RoleRelatedInterviewScheduledForm from './shared/forms/InterviewScheduledForm/RoleRelatedInterviewScheduledForm';
import { IconMenuItem } from './shared/IconMenu';
import { SvgIconProps } from '@mui/material';
import { Stages } from 'domain/shared/candidate';
import { VacancyPriority } from './types';
export const VacancyPriorityShortLabels = {
  [VacancyPriority.HIGH]: 'H',
  [VacancyPriority.MEDIUM]: 'M',
  [VacancyPriority.LOW]: 'L',
};

export const StagesOptions = {
  [Stages.Applied]: 'Applied',
  [Stages.Screening]: 'Screening',
  [Stages.Interview]: 'Interview',
  [Stages.Decision]: 'Decision',
  [Stages.Offer]: 'Offer',
  [Stages.Hired]: 'Hired',
  [Stages.PendingRejection]: 'Pending rejection',
};

export const MoveToStages: Record<Exclude<Stages, Stages.Rejected>, string> = {
  [Stages.Applied]: 'MoveToApplied',
  [Stages.Screening]: 'MoveToScreening',
  [Stages.Interview]: 'MoveToInterview',
  [Stages.Decision]: 'MoveToDecision',
  [Stages.Offer]: 'MoveToOffer',
  [Stages.Hired]: 'MoveToHired',
  [Stages.PendingRejection]: 'MoveToReject',
  [Stages.Closed]: 'MoveToClosed',
};

const SECOND = 1000;
export const MINUTE = 60 * SECOND;
const HOUR = MINUTE * 60;

export const RECRUITING_SLA_SCHEDULE = {
  timezone: 'Europe/Minsk',
  workdayStartHour: 9,
  workdayEndHour: 18,
} as const;

export const RECRUITING_SLA_WORKING_DAY =
  HOUR * (RECRUITING_SLA_SCHEDULE.workdayEndHour - RECRUITING_SLA_SCHEDULE.workdayStartHour);

export const columnNames = [
  {
    name: 'Applied',
    stage: Stages.Applied,
    deadlinesForStage: {
      normal: HOUR * 3,
      warning: HOUR * 5,
    },
  },
  {
    name: 'Screening',
    stage: Stages.Screening,
    deadlinesForStage: {
      normal: RECRUITING_SLA_WORKING_DAY * 4,
      warning: RECRUITING_SLA_WORKING_DAY * 5,
    },
  },
  {
    name: 'Interview',
    stage: Stages.Interview,
    deadlinesForStage: {
      normal: RECRUITING_SLA_WORKING_DAY * 4,
      warning: RECRUITING_SLA_WORKING_DAY * 5,
    },
  },
  {
    name: 'Decision',
    stage: Stages.Decision,
    deadlinesForStage: {
      normal: RECRUITING_SLA_WORKING_DAY,
      warning: RECRUITING_SLA_WORKING_DAY * 2,
    },
  },
  {
    name: 'Offer',
    stage: Stages.Offer,
    deadlinesForStage: {
      normal: RECRUITING_SLA_WORKING_DAY * 3,
      warning: RECRUITING_SLA_WORKING_DAY * 4,
    },
  },
  {
    name: 'Hired',
    stage: Stages.Hired,
  },
  {
    name: 'Pending reject',
    stage: Stages.PendingRejection,
  },
];

export const TaskDeadlineStyle = {
  Normal: {
    borderColor: 'border.success',
    dragIconBackground: 'success.main',
  },
  Warning: {
    borderColor: 'border.warning',
    dragIconBackground: 'warning.main',
  },
  Expired: {
    borderColor: 'border.error',
    dragIconBackground: 'error.main',
  },
} as const;

export const stepConfigurations: Record<
  string,
  {
    icon: React.ElementType<SvgIconProps>;
    job: string;
    states: Array<
      IconMenuItem & {
        event: string;
      }
    >;
  }[]
> = {
  Offer: [
    {
      icon: DockIcon,
      job: 'OfferSending',
      states: [
        {
          key: 'OfferNotSent',
          text: 'Offer is not sent',
          event: 'OfferReset',
        },
        {
          key: 'OfferSent',
          text: 'Offer is sent',
          event: 'OfferSent',
          color: 'success',
          modal: OfferForm as unknown as React.ReactNode,
        },
      ],
    },
  ],
  Hired: [
    {
      icon: HLetterIcon,
      job: 'FirstWorkingDay',
      states: [
        {
          key: 'FirstWorkingDayNotDefined',
          text: "Candidate's start date is expected",
          event: 'FirstWorkingDayReset',
          tooltipType: 'Hired',
        },
        {
          key: 'FirstWorkingDayProvided',
          text: "Candidate's start date provided",
          event: 'FirstWorkingDayProvided',
          color: 'warning',
          tooltipType: 'Hired',
          isManagedByProcess: true,
        },
        {
          key: 'FirstWorkingDayStarted',
          event: 'FirstWorkingDayStarted',
          text: 'Candidate has started',
          color: 'success',
          tooltipType: 'Hired',
          isManagedByProcess: true,
        },
      ],
    },
    {
      icon: BLetterIcon,
      job: 'BootcampPeriod',
      states: [
        {
          key: 'BootcampPeriodPending',
          text: 'Bootcamp',
          event: 'BootcampPeriodReset',
        },
        {
          key: 'BootcampPeriodFinished',
          event: 'BootcampPeriodFinished',
          text: 'Bootcamp finished positively',
          color: 'success',
          isManagedByProcess: true,
        },
      ],
    },
    {
      icon: PLetterIcon,
      job: 'ProbationPeriod',
      states: [
        {
          key: 'ProbationPeriodPending',
          text: 'Probation period',
          event: 'ProbationPeriodReset',
        },
        {
          key: 'ProbationPeriodFinished',
          text: 'Probation finished positively',
          event: 'ProbationPeriodFinished',
          color: 'success',
          isManagedByProcess: true,
        },
      ],
    },
  ],
  Interview: [
    {
      icon: RoleRelatedIcon,
      job: 'RoleRelatedInterview',
      states: [
        {
          key: 'RoleRelatedInterviewPending',
          text: 'Role-related interview is not scheduled',
          event: 'RoleRelatedInterviewReset',
        },
        {
          key: 'RoleRelatedInterviewScheduled',
          text: 'Role-related interview is scheduled',
          event: 'RoleRelatedInterviewScheduled',
          color: 'warning',
          modal: RoleRelatedInterviewScheduledForm as unknown as React.ReactNode,
          tooltipType: 'RoleRelatedInterview',
        },
        {
          key: 'RoleRelatedInterviewCompleted',
          event: 'RoleRelatedInterviewCompleted',
          text: 'Role-related interview is conducted',
          color: 'success',
          isManagedByProcess: true,
        },
      ],
    },
    {
      icon: GeneralInterviewIcon,
      job: 'GeneralInterview',
      states: [
        {
          key: 'GeneralInterviewPending',
          event: 'GeneralInterviewReset',
          text: 'General interview is not scheduled',
        },
        {
          key: 'GeneralInterviewScheduled',
          event: 'GeneralInterviewScheduled',
          text: 'General interview is scheduled',
          color: 'warning',
          modal: GeneralInterviewScheduledForm as unknown as React.ReactNode,
          tooltipType: 'GeneralInterview',
        },
        {
          key: 'GeneralInterviewCompleted',
          event: 'GeneralInterviewCompleted',
          text: 'General interview is conducted',
          color: 'success',
          isManagedByProcess: true,
        },
      ],
    },
    {
      icon: ALetterIcon,
      job: 'AdditionalActivities',
      states: [
        {
          key: 'AdditionalActivitiesNotRequired',
          event: 'AdditionalActivitiesReset',
          text: 'Additional activities not required',
        },
        {
          key: 'AdditionalActivitiesRequested',
          text: 'Additional activities required',
          event: 'AdditionalActivitiesRequested',
          color: 'warning',
          modal: AdditionalActivitiesForm as unknown as React.ReactNode,
          tooltipType: 'AdditionalActivities',
        },
        {
          key: 'AdditionalActivitiesReceived',
          text: 'Additional activities received',
          event: 'AdditionalActivitiesReceived',
          color: 'success',
          isManagedByProcess: true,
        },
      ],
    },
  ],
  Screening: [
    {
      icon: ChecklistIcon,
      job: 'ScreeningChecklist',
      states: [
        {
          key: 'ScreeningChecklistPending',
          event: 'ScreeningChecklistReset',
          text: 'Checklist not sent to candidate',
        },
        {
          key: 'ScreeningChecklistSent',
          event: 'ScreeningChecklistSent',
          text: 'Checklist sent to candidate',
          color: 'warning',
        },
        {
          key: 'ScreeningChecklistCompleted',
          event: 'ScreeningChecklistCompleted',
          text: 'Checklist received from candidate',
          color: 'success',
          modal: CompensationForm as unknown as React.ReactNode,
        },
      ],
    },
    {
      icon: TestTaskIcon,
      job: 'ScreeningTestTask',
      states: [
        {
          key: 'ScreeningTestTaskPending',
          event: 'ScreeningTestTaskReset',
          text: 'Test task not sent to candidate',
        },
        {
          key: 'ScreeningTestTaskSent',
          event: 'ScreeningTestTaskSent',
          text: 'Test task sent to candidate',
          color: 'warning',
        },
        {
          key: 'ScreeningTestTaskCompleted',
          event: 'ScreeningTestTaskCompleted',
          text: 'Test task received from candidate',
          color: 'success',
        },
      ],
    },
    {
      icon: CalendarMonthIcon,
      job: 'ScreeningCommunication',
      states: [
        {
          key: 'ScreeningCommunicationPending',
          text: 'Screening call not scheduled with candidate',
          event: 'ScreeningCommunicationReset',
        },
        {
          key: 'ScreeningCommunicationScheduled',
          text: 'Screening call scheduled with candidate',
          event: 'ScreeningCommunicationScheduled',
          color: 'warning',
          modal: ScreeningForm as unknown as React.ReactNode,
          tooltipType: 'ScreeningCommunication',
        },
        {
          key: 'ScreeningCommunicationCompleted',
          event: 'ScreeningCommunicationCompleted',
          text: 'Screening call conducted with candidate',
          color: 'success',
        },
      ],
    },
  ],
};

const REJECTION_REASON = {
  // Applied stage
  APPLIED_NO_RELEVANT_POSITION: 'APPLIED_NO_RELEVANT_POSITION',
  APPLIED_NO_RELEVANT_EXPERIENCE: 'APPLIED_NO_RELEVANT_EXPERIENCE',
  APPLIED_INSUFFICIENT_ENGLISH_LEVEL: 'APPLIED_INSUFFICIENT_ENGLISH_LEVEL',
  APPLIED_RELOCATION_REGISTRATION_ISSUES: 'APPLIED_RELOCATION_REGISTRATION_ISSUES',

  // Screening stage
  CHECKLIST_SCREENING_NO_RESPONSE: 'CHECKLIST_SCREENING_NO_RESPONSE',
  CHECKLIST_SCREENING_EXPERIENCE_MISMATCH: 'CHECKLIST_SCREENING_EXPERIENCE_MISMATCH',
  CHECKLIST_SCREENING_ENGLISH_LEVEL_MISMATCH: 'CHECKLIST_SCREENING_ENGLISH_LEVEL_MISMATCH',
  CHECKLIST_SCREENING_RELOCATION_REGISTRATION_ISSUES: 'CHECKLIST_SCREENING_RELOCATION_REGISTRATION_ISSUES',
  CHECKLIST_SCREENING_WORK_SCHEDULE_NOT_SUITABLE: 'CHECKLIST_SCREENING_WORK_SCHEDULE_NOT_SUITABLE',
  CHECKLIST_SCREENING_SALARY_EXPECTATIONS_MISMATCH: 'CHECKLIST_SCREENING_SALARY_EXPECTATIONS_MISMATCH',
  TEST_TASK_SCREENING_POOR_PERFORMANCE: 'TEST_TASK_SCREENING_POOR_PERFORMANCE',
  TEST_TASK_SCREENING_NOT_SUBMITTED: 'TEST_TASK_SCREENING_NOT_SUBMITTED',
  TEST_TASK_SCREENING_DECLINED: 'TEST_TASK_SCREENING_DECLINED',
  CALL_SCREENING_INSUFFICIENT_TECHNICAL_SKILLS: 'CALL_SCREENING_INSUFFICIENT_TECHNICAL_SKILLS',
  CALL_SCREENING_INSUFFICIENT_SOFT_SKILLS_MOTIVATION: 'CALL_SCREENING_INSUFFICIENT_SOFT_SKILLS_MOTIVATION',
  CALL_SCREENING_COMPANY_CULTURE_MISMATCH: 'CALL_SCREENING_COMPANY_CULTURE_MISMATCH',
  CALL_SCREENING_ENGLISH_LEVEL_MISMATCH: 'CALL_SCREENING_ENGLISH_LEVEL_MISMATCH',

  // Interview stage
  GENERAL_INTERVIEW_SOFT_SKILLS_MOTIVATION_MISMATCH: 'GENERAL_INTERVIEW_SOFT_SKILLS_MOTIVATION_MISMATCH',
  GENERAL_INTERVIEW_COMPANY_CULTURE_MISMATCH: 'GENERAL_INTERVIEW_COMPANY_CULTURE_MISMATCH',
  GENERAL_INTERVIEW_INSUFFICIENT_ENGLISH_LEVEL: 'GENERAL_INTERVIEW_INSUFFICIENT_ENGLISH_LEVEL',
  GENERAL_INTERVIEW_TECHNICAL_SKILLS_MISMATCH: 'GENERAL_INTERVIEW_TECHNICAL_SKILLS_MISMATCH',
  ROLE_INTERVIEW_TECHNICAL_SKILLS_MISMATCH: 'ROLE_INTERVIEW_TECHNICAL_SKILLS_MISMATCH',
  ROLE_INTERVIEW_SOFT_SKILLS_MOTIVATION_MISMATCH: 'ROLE_INTERVIEW_SOFT_SKILLS_MOTIVATION_MISMATCH',
  ROLE_INTERVIEW_COMPANY_CULTURE_MISMATCH: 'ROLE_INTERVIEW_COMPANY_CULTURE_MISMATCH',
  ROLE_INTERVIEW_INSUFFICIENT_ENGLISH_LEVEL: 'ROLE_INTERVIEW_INSUFFICIENT_ENGLISH_LEVEL',
  ROLE_INTERVIEW_TECHNICAL_SOFT_SKILLS_MISMATCH: 'ROLE_INTERVIEW_TECHNICAL_SOFT_SKILLS_MISMATCH',

  // Decision stage
  DECISION_BUDGET_LIMITATIONS: 'DECISION_BUDGET_LIMITATIONS',
  DECISION_POSITION_NO_LONGER_NEEDED: 'DECISION_POSITION_NO_LONGER_NEEDED',
  DECISION_INSUFFICIENT_SENIORITY_LEVEL: 'DECISION_INSUFFICIENT_SENIORITY_LEVEL',
  // Offer stage
  OFFER_DEVELOPMENT_PROSPECTS_CONCERNS: 'OFFER_DEVELOPMENT_PROSPECTS_CONCERNS',
  OFFER_SPECIFIC_PREFERENCES: 'OFFER_SPECIFIC_PREFERENCES',
  OFFER_SALARY_EXPECTATIONS_NOT_ALIGNED: 'OFFER_SALARY_EXPECTATIONS_NOT_ALIGNED',
  OFFER_SPECIFIC_PREFERENCES_AND_SALARY: 'OFFER_SPECIFIC_PREFERENCES_AND_SALARY',
  OFFER_DEVELOPMENT_PROSPECTS_AND_SALARY: 'OFFER_DEVELOPMENT_PROSPECTS_AND_SALARY',
  OFFER_RELOCATION_REGISTRATION_ISSUES: 'OFFER_RELOCATION_REGISTRATION_ISSUES',
  OFFER_IS_CANCELLED: 'OFFER_IS_CANCELLED',
  OFFER_IS_NOT_SENT: 'OFFER_IS_NOT_SENT',
  OFFER_NO_INFO: 'OFFER_NO_INFO',
  // Hiring stage
  HIRING_RELOCATION_REGISTRATION_ISSUES: 'HIRING_RELOCATION_REGISTRATION_ISSUES',
  HIRING_COMPANY_CULTURE_MISMATCH: 'HIRING_COMPANY_CULTURE_MISMATCH',
  HIRING_PROJECT_TASKS_MISMATCH: 'HIRING_PROJECT_TASKS_MISMATCH',
  HIRING_CAREER_GROWTH_LIMITATIONS: 'HIRING_CAREER_GROWTH_LIMITATIONS',
  HIRING_CLIENT_RELATED_CONCERNS: 'HIRING_CLIENT_RELATED_CONCERNS',
  HIRING_SALARY_CONCERNS: 'HIRING_SALARY_CONCERNS',
  HIRING_BURNOUT: 'HIRING_BURNOUT',
  HIRING_MANAGER_RELATED_ISSUES: 'HIRING_MANAGER_RELATED_ISSUES',
  HIRING_SOFT_SKILLS_MOTIVATION_MISMATCH: 'HIRING_SOFT_SKILLS_MOTIVATION_MISMATCH',
  HIRING_TECHNICAL_SKILLS_MISMATCH: 'HIRING_TECHNICAL_SKILLS_MISMATCH',
  HIRING_TECHNICAL_AND_SOFT_SKILLS_MISMATCH: 'HIRING_TECHNICAL_AND_SOFT_SKILLS_MISMATCH',
  HIRING_WORK_SCHEDULE_INCOMPATIBILITY: 'HIRING_WORK_SCHEDULE_INCOMPATIBILITY',
  BACKGROUND_CHECK_PROCESS_FAILED: 'BACKGROUND_CHECK_PROCESS_FAILED',
  // Common
  COMMON_POSITION_IS_CLOSED: 'COMMON_POSITION_IS_CLOSED',
  COMMON_NO_RESPONSE: 'COMMON_NO_RESPONSE',
  COMMON_NOT_INTERESTED: 'COMMON_NOT_INTERESTED',
};

export const REJECTION_REASONS_BY_STAGE = {
  [Stages.Applied]: {
    [REJECTION_REASON.APPLIED_NO_RELEVANT_POSITION]: 'No relevant position available',
    [REJECTION_REASON.APPLIED_NO_RELEVANT_EXPERIENCE]:
      'No relevant experience — the candidate does not meet the requirements',
    [REJECTION_REASON.APPLIED_INSUFFICIENT_ENGLISH_LEVEL]: 'Insufficient English level',
    [REJECTION_REASON.APPLIED_RELOCATION_REGISTRATION_ISSUES]: 'Issues with relocation, registration, or location',
    // Common
    [REJECTION_REASON.COMMON_POSITION_IS_CLOSED]: 'The position is closed',
    [REJECTION_REASON.COMMON_NO_RESPONSE]: 'No response from the candidate',
    [REJECTION_REASON.COMMON_NOT_INTERESTED]: 'The Candidate is not interested',
  },
  [Stages.Screening]: {
    [REJECTION_REASON.CHECKLIST_SCREENING_NO_RESPONSE]: 'No response after sending the checklist',
    [REJECTION_REASON.CHECKLIST_SCREENING_EXPERIENCE_MISMATCH]: 'Relevant experience mismatch based on the checklist',
    [REJECTION_REASON.CHECKLIST_SCREENING_ENGLISH_LEVEL_MISMATCH]: 'English level mismatch based on the checklist',
    [REJECTION_REASON.CHECKLIST_SCREENING_RELOCATION_REGISTRATION_ISSUES]:
      'Relocation/registration issues based on the checklist',
    [REJECTION_REASON.CHECKLIST_SCREENING_WORK_SCHEDULE_NOT_SUITABLE]:
      'Work schedule not suitable based on the checklist',
    [REJECTION_REASON.CHECKLIST_SCREENING_SALARY_EXPECTATIONS_MISMATCH]:
      'Salary expectations do not match based on the checklist',
    [REJECTION_REASON.TEST_TASK_SCREENING_POOR_PERFORMANCE]: 'Poor performance on the test task',
    [REJECTION_REASON.TEST_TASK_SCREENING_NOT_SUBMITTED]: 'Test task not submitted',
    [REJECTION_REASON.TEST_TASK_SCREENING_DECLINED]: 'Declined to take the test task',
    [REJECTION_REASON.CALL_SCREENING_INSUFFICIENT_TECHNICAL_SKILLS]:
      'Insufficient technical skills during the screening call',
    [REJECTION_REASON.CALL_SCREENING_INSUFFICIENT_SOFT_SKILLS_MOTIVATION]:
      'Insufficient soft skills or motivation during the screening call',
    [REJECTION_REASON.CALL_SCREENING_COMPANY_CULTURE_MISMATCH]: 'Company culture mismatch during the screening call',
    [REJECTION_REASON.CALL_SCREENING_ENGLISH_LEVEL_MISMATCH]: 'English level mismatch during the screening call',
    // Common
    [REJECTION_REASON.COMMON_POSITION_IS_CLOSED]: 'The position is closed',
    [REJECTION_REASON.COMMON_NO_RESPONSE]: 'No response from the candidate',
    [REJECTION_REASON.COMMON_NOT_INTERESTED]: 'The Candidate is not interested',
  },
  [Stages.Interview]: {
    [REJECTION_REASON.GENERAL_INTERVIEW_TECHNICAL_SKILLS_MISMATCH]:
      'General interview - Technical skills do not meet the requirements',
    [REJECTION_REASON.GENERAL_INTERVIEW_SOFT_SKILLS_MOTIVATION_MISMATCH]:
      'General interview - Soft skills and motivation do not meet the requirements',
    [REJECTION_REASON.GENERAL_INTERVIEW_COMPANY_CULTURE_MISMATCH]: 'General interview - Company culture mismatch',
    [REJECTION_REASON.GENERAL_INTERVIEW_INSUFFICIENT_ENGLISH_LEVEL]: 'General interview - Insufficient English level',
    [REJECTION_REASON.ROLE_INTERVIEW_TECHNICAL_SKILLS_MISMATCH]:
      'Role-related interview - Technical skills do not meet the requirements',
    [REJECTION_REASON.ROLE_INTERVIEW_SOFT_SKILLS_MOTIVATION_MISMATCH]:
      'Role-related interview - Soft skills and motivation do not meet the requirements',
    [REJECTION_REASON.ROLE_INTERVIEW_COMPANY_CULTURE_MISMATCH]: 'Role-related interview - Company culture mismatch',
    [REJECTION_REASON.ROLE_INTERVIEW_INSUFFICIENT_ENGLISH_LEVEL]: 'Role-related interview - Insufficient English level',
    [REJECTION_REASON.ROLE_INTERVIEW_TECHNICAL_SOFT_SKILLS_MISMATCH]:
      'Role-related interview - Both technical and soft skills do not meet the requirements',
    // Common
    [REJECTION_REASON.COMMON_POSITION_IS_CLOSED]: 'The position is closed',
    [REJECTION_REASON.COMMON_NO_RESPONSE]: 'No response from the candidate',
    [REJECTION_REASON.COMMON_NOT_INTERESTED]: 'The Candidate is not interested',
  },
  [Stages.Decision]: {
    [REJECTION_REASON.DECISION_BUDGET_LIMITATIONS]: 'Budget limitations',
    [REJECTION_REASON.DECISION_POSITION_NO_LONGER_NEEDED]: 'Position no longer needed',
    [REJECTION_REASON.DECISION_INSUFFICIENT_SENIORITY_LEVEL]: 'Insufficient seniority level',
    // Common
    [REJECTION_REASON.COMMON_POSITION_IS_CLOSED]: 'The position is closed',
    [REJECTION_REASON.COMMON_NO_RESPONSE]: 'No response from the candidate',
    [REJECTION_REASON.COMMON_NOT_INTERESTED]: 'The Candidate is not interested',
  },
  [Stages.Offer]: {
    [REJECTION_REASON.OFFER_DEVELOPMENT_PROSPECTS_CONCERNS]: 'Concerns about development prospects',
    [REJECTION_REASON.OFFER_SPECIFIC_PREFERENCES]: 'Specific preferences of the candidate',
    [REJECTION_REASON.OFFER_SALARY_EXPECTATIONS_NOT_ALIGNED]: 'Salary expectations not aligned',
    [REJECTION_REASON.OFFER_SPECIFIC_PREFERENCES_AND_SALARY]: 'Specific preferences + Salary',
    [REJECTION_REASON.OFFER_DEVELOPMENT_PROSPECTS_AND_SALARY]: 'Development prospects + Salary',
    [REJECTION_REASON.OFFER_RELOCATION_REGISTRATION_ISSUES]: 'Relocation or registration issues',
    [REJECTION_REASON.OFFER_IS_CANCELLED]: 'Offer is cancelled',
    [REJECTION_REASON.OFFER_IS_NOT_SENT]: 'Offer is not sent',
    [REJECTION_REASON.OFFER_NO_INFO]: 'No info',
    // Common
    [REJECTION_REASON.COMMON_POSITION_IS_CLOSED]: 'The position is closed',
    [REJECTION_REASON.COMMON_NO_RESPONSE]: 'No response from the candidate',
    [REJECTION_REASON.COMMON_NOT_INTERESTED]: 'The Candidate is not interested',
  },
  [Stages.Hired]: {
    [REJECTION_REASON.HIRING_RELOCATION_REGISTRATION_ISSUES]: 'Issues with relocation or registration',
    [REJECTION_REASON.HIRING_COMPANY_CULTURE_MISMATCH]: 'Company culture mismatch',
    [REJECTION_REASON.HIRING_PROJECT_TASKS_MISMATCH]: 'Mismatch with project tasks',
    [REJECTION_REASON.HIRING_CAREER_GROWTH_LIMITATIONS]: 'Lack of career growth and development opportunities',
    [REJECTION_REASON.HIRING_CLIENT_RELATED_CONCERNS]: 'Client-related concerns',
    [REJECTION_REASON.HIRING_SALARY_CONCERNS]: 'Salary concerns',
    [REJECTION_REASON.HIRING_BURNOUT]: 'Burnout',
    [REJECTION_REASON.HIRING_MANAGER_RELATED_ISSUES]: 'Manager-related issues',
    [REJECTION_REASON.HIRING_SOFT_SKILLS_MOTIVATION_MISMATCH]: 'Soft skills and motivation do not meet expectations',
    [REJECTION_REASON.HIRING_TECHNICAL_SKILLS_MISMATCH]: 'Technical skills do not meet expectations',
    [REJECTION_REASON.HIRING_TECHNICAL_AND_SOFT_SKILLS_MISMATCH]:
      'Both technical and soft skills do not meet expectations',
    [REJECTION_REASON.HIRING_WORK_SCHEDULE_INCOMPATIBILITY]: 'Work schedule incompatibility',
    [REJECTION_REASON.BACKGROUND_CHECK_PROCESS_FAILED]: 'Background check process failed',
    // Common
    [REJECTION_REASON.COMMON_POSITION_IS_CLOSED]: 'The position is closed',
    [REJECTION_REASON.COMMON_NO_RESPONSE]: 'No response from the candidate',
    [REJECTION_REASON.COMMON_NOT_INTERESTED]: 'The Candidate is not interested',
  },
};
